import { SportName } from '@shared/enums/SportName';
import { Context } from '@shared/enums/Context';
import { getCategoryChild, getLeagueName, getLocation, getMatchScores, getSportName, isMatch } from '@shared/utils/eventUtils';

const getCompetitionStageLabel = (event) => event.subStageLabel || event.stageGroupLabel || null;

const getCompetitionStageInfo = (event) => {
	const competitionStageLabel = getCompetitionStageLabel(event);

	if (!competitionStageLabel) {
		return null;
	}

	return {
		label: competitionStageLabel,
	};
};

const getVariant = (event) => {
	const matchScores = getMatchScores(event);
	const hasMatchParticipants = Boolean(matchScores.homeNameFull && matchScores.guestNameFull);

	if (isMatch(event) && event.sport?.name === SportName.VOLLEYBALL) {
		return 'volley';
	}

	if (isMatch(event) && hasMatchParticipants) {
		return 'match';
	}

	return 'single';
};

const getParticipantMeta = (participant) => {
	const participatable = participant?.participatable;
	const teamName = participatable?.team?.name || null;
	const clubName = participatable?.team?.club?.name || participatable?.team?.clubTeamName || null;
	return [teamName, clubName].filter(Boolean).join(' · ') || null;
};

const getParticipants = (event) => {
	return (event.participants || [])
		.map(participant => ({
			id:       participant.id,
			name:     participant.participatable?.name || null,
			imageUrl: participant.participatable?.imageUrl || participant.participatable?.team?.imageUrl || null,
			meta:     getParticipantMeta(participant),
		}))
		.filter(participant => participant.name);
};

const getMetaItems = (event) => {
	const locationName = getLocation(event) || event.venue?.name || null;
	return [
		event.subtitle || null,
		locationName,
	].filter(Boolean);
};

// Resolve through the filter options so the card uses the same localized label as the filter control.
const getTeamCategoryLabel = (event, teamCategoryFilter) => {
	if (!teamCategoryFilter?.enabled) {
		return null;
	}

	const participant = event.participants?.find(item => item.participatable?.category || item.participatable?.team?.category);
	const participantTeamCategory = participant?.participatable?.category || participant?.participatable?.team?.category || null;
	const teamCategory = event.teamCategory || event.team_category || event.team?.category || event.teamHome?.category || event.teamGuest?.category || participantTeamCategory;

	if (!teamCategory) {
		return null;
	}

	return teamCategoryFilter.options?.find(option => String(option.value) === String(teamCategory))?.label || null;
};

// Context labels should add orientation without repeating the selected competition or league.
const getContextLabel = (event, options) => {
	if (options.isCompetitionView) {
		return null;
	}

	if (options.contextType === Context.CLIENT) {
		return getSportName(event);
	}

	if (options.contextType === Context.FEDERATION) {
		return window.$arenaConfig.hideEventCardSportTag
			? getLeagueName(event)
			: getSportName(event);
	}

	return null;
};

export const EventListItemProps = (event, _options = {}) => {
	const options = {
		contextType:              null,
		isCompetitionView:        false,
		hideCompetitionStageInfo: false,
		teamCategoryFilter:       null,
		..._options,
	};
	const variant = getVariant(event);

	return {
		variant,
		title:                event.name || event.title || $I18n.trans('commons.unnamed'),
		subtitle:             variant === 'single' ? null : event.subtitle || null,
		contextLabel:         getContextLabel(event, options),
		teamCategoryLabel:    getTeamCategoryLabel(event, options.teamCategoryFilter),
		sportLabel:           variant === 'single' ? getSportName(event) : null,
		disciplineLabel:      variant === 'single' ? getCategoryChild(event) : null,
		metaItems:            variant === 'single' ? getMetaItems(event) : [],
		participants:         variant === 'single' ? getParticipants(event) : [],
		unixStart:            event.startTimestamp,
		unixKickoff:          event.startTimestamp + (event.kickoffOffsetMinutes * 60),
		unixEnd:              event.endTimestamp,
		eventType:            event.type,
		link:                 event.externalEventLink || event.url,
		isExternal:           Boolean(event.externalEventLink),
		competitionStageInfo: options.hideCompetitionStageInfo ? null : getCompetitionStageInfo(event),
		numberOfPeriods:      getMatchScores(event).scores?.length || 0,
		dashScoreDevider:     window.$arenaConfig.isEuf,
		...getMatchScores(event),
	};
};
